import React from 'react';
import { Alert, Box, Button, Container, Link, Paper, TextField, Typography } from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

const PasswordResetPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = React.useState('');
  const [passwordConfirmation, setPasswordConfirmation] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [completed, setCompleted] = React.useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    if (password !== passwordConfirmation) {
      setError('Die eingegebenen Passwörter stimmen nicht überein.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch('/api/password-reset/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Das Passwort konnte nicht zurückgesetzt werden.');
      setCompleted(true);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Verbindung zum Server fehlgeschlagen.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: '#4277fc', p: 2 }}>
      <Container component="main" maxWidth="xs">
        <Paper elevation={4} sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3 }}>
          <Typography component="h1" variant="h5" sx={{ mb: 1 }}>
            Neues Passwort festlegen
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Wählen Sie ein neues Passwort für Ihr caix Kundenportal.
          </Typography>
          {completed ? (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>Ihr Passwort wurde erfolgreich zurückgesetzt.</Alert>
              <Button component={RouterLink} to="/login" variant="contained" fullWidth>
                Zur Anmeldung
              </Button>
            </>
          ) : !token ? (
            <>
              <Alert severity="error" sx={{ mb: 2 }}>Der Link zum Zurücksetzen ist ungültig oder unvollständig.</Alert>
              <Link component={RouterLink} to="/login">Zurück zur Anmeldung</Link>
            </>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              <TextField
                autoFocus
                required
                fullWidth
                margin="normal"
                label="Neues Passwort"
                type="password"
                autoComplete="new-password"
                inputProps={{ minLength: 8, maxLength: 72 }}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={submitting}
              />
              <TextField
                required
                fullWidth
                margin="normal"
                label="Neues Passwort wiederholen"
                type="password"
                autoComplete="new-password"
                inputProps={{ minLength: 8, maxLength: 72 }}
                value={passwordConfirmation}
                onChange={(event) => setPasswordConfirmation(event.target.value)}
                disabled={submitting}
              />
              <Button type="submit" variant="contained" fullWidth disabled={submitting} sx={{ mt: 3 }}>
                {submitting ? 'Wird gespeichert …' : 'Passwort zurücksetzen'}
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default PasswordResetPage;
